<template>
  <v-row no-gutters>
    <v-col cols="12">
      <app-ag-grid
        title="Chart of Accounts"
        :columnDefs="columnDefs"
        :rowData="accounts"
        :loading="loading"
        :mobileCard="mobileCard"
        @refresh="fetchAccounts"
        @add="onAdd"
      ></app-ag-grid>
    </v-col>

    <GlobalDialog
      v-if="formDialog"
      v-model="formDialog"
      :title="editing ? 'Edit Account' : 'Add Account'"
      icon="mdi-book-open-variant-outline"
      :width="480"
      :buttons="formButtons"
    >
      <v-form ref="accountForm" v-model="formValid">
        <v-text-field
          v-model="form.code"
          label="Code"
          outlined
          dense
          :disabled="!!editing"
          :rules="[rules.required]"
          hint="A short, stable identifier — e.g. 6100"
          persistent-hint
          prepend-inner-icon="mdi-pound"
        />
        <v-text-field
          v-model="form.name"
          label="Name"
          outlined
          dense
          class="mt-4"
          :disabled="editing && editing.isSystem"
          :rules="[rules.required]"
          prepend-inner-icon="mdi-tag-outline"
        />
        <v-select
          v-model="form.type"
          :items="typeOptions"
          label="Type"
          outlined
          dense
          class="mt-4"
          :disabled="!!editing"
          :rules="[rules.required]"
          prepend-inner-icon="mdi-shape-outline"
          @change="onTypeChange"
        />
        <v-select
          v-model="form.subType"
          :items="subTypeOptionsForType"
          label="Sub-type"
          outlined
          dense
          class="mt-4"
          :disabled="!!editing"
          :rules="[rules.required]"
          prepend-inner-icon="mdi-shape-plus-outline"
        />
        <v-textarea
          v-model="form.description"
          label="Description (optional)"
          outlined
          dense
          rows="2"
          class="mt-4"
          :disabled="editing && editing.isSystem"
          prepend-inner-icon="mdi-note-text-outline"
        />
      </v-form>
    </GlobalDialog>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import GlobalDialog from "./common/global-dialog.vue";
import { subTypeLabel } from "@/utils/account-labels";

const TYPE_LABELS = {
  ASSET: "Asset",
  LIABILITY: "Liability",
  EQUITY: "Equity",
  INCOME: "Income",
  EXPENSE: "Expense",
};

const SUBTYPES_BY_TYPE = {
  ASSET: ["CURRENT_ASSET", "BANK", "CASH", "ACCOUNTS_RECEIVABLE", "INVENTORY", "FIXED_ASSET", "OTHER_ASSET"],
  LIABILITY: ["CURRENT_LIABILITY", "ACCOUNTS_PAYABLE", "TAX_PAYABLE", "LOAN", "OTHER_LIABILITY"],
  EQUITY: ["OWNER_EQUITY", "RETAINED_EARNINGS", "CURRENT_YEAR_EARNINGS"],
  INCOME: ["OPERATING_INCOME", "OTHER_INCOME"],
  EXPENSE: ["COGS", "OPERATING_EXPENSE", "TAX_EXPENSE", "OTHER_EXPENSE"],
};

export default {
  name: "ChartOfAccountsPage",
  components: { AppAgGrid, GlobalDialog },

  data() {
    return {
      loading: false,
      saving: false,
      formDialog: false,
      formValid: false,
      editing: null,

      form: {
        code: "",
        name: "",
        type: "",
        subType: "",
        description: "",
      },

      rules: {
        required: (v) => !!v || "Required",
      },

      columnDefs: [
        { headerName: "Code", field: "code", width: 100 },
        { headerName: "Name", field: "name", minWidth: 200 },
        {
          headerName: "Type",
          field: "type",
          width: 120,
          valueFormatter: (p) => TYPE_LABELS[p.value] || p.value,
        },
        {
          headerName: "Sub-type",
          field: "subType",
          minWidth: 160,
          valueFormatter: (p) => subTypeLabel(p.value),
        },
        {
          headerName: "Normal Balance",
          field: "normalBalance",
          width: 140,
        },
        {
          headerName: "System",
          field: "isSystem",
          width: 100,
          valueFormatter: (p) => (p.value ? "Yes" : "-"),
        },
        {
          headerName: "Status",
          field: "archived",
          width: 110,
          valueFormatter: (p) => (p.value ? "Archived" : "Active"),
        },
        {
          headerName: "Actions",
          field: "_id",
          width: 160,
          cellRenderer: (params) => {
            const archiveLabel = params.data.archived ? "Unarchive" : "Archive";
            return `
              <i
                class="mdi mdi-pencil-outline coa-edit"
                data-id="${params.data._id}"
                title="Edit"
                style="cursor:pointer;font-size:18px;color:#0a5a90;margin-right:14px;"
              ></i>
              <i
                class="mdi mdi-archive-outline coa-archive"
                data-id="${params.data._id}"
                title="${archiveLabel}"
                style="cursor:pointer;font-size:18px;color:#64748b;"
              ></i>
            `;
          },
          onCellClicked: (params) => {
            if (params.event.target.classList.contains("coa-edit")) {
              this.openEdit(params.data);
            } else if (params.event.target.classList.contains("coa-archive")) {
              this.toggleArchive(params.data);
            }
          },
        },
      ],
    };
  },

  computed: {
    accounts() {
      return this.$store.state.accounts.accounts;
    },

    typeOptions() {
      return Object.entries(TYPE_LABELS).map(([value, text]) => ({ value, text }));
    },

    subTypeOptionsForType() {
      const subTypes = SUBTYPES_BY_TYPE[this.form.type] || [];
      return subTypes.map((value) => ({ value, text: subTypeLabel(value) }));
    },

    formButtons() {
      return [
        {
          text: "Cancel",
          outlined: true,
          color: "#0a5a90",
          action: () => (this.formDialog = false),
        },
        {
          text: this.editing ? "Save" : "Add Account",
          loading: this.saving,
          disabled: !this.formValid,
          action: this.submit,
        },
      ];
    },

    mobileCard() {
      return {
        title: (row) => `${row.code} — ${row.name}`,
        subtitle: (row) => `${TYPE_LABELS[row.type] || row.type} · ${subTypeLabel(row.subType)}`,
        badge: (row) => ({
          text: row.archived ? "Archived" : "Active",
          style: row.archived
            ? { background: "#f1f5f9", color: "#64748b" }
            : { background: "#e6f7ec", color: "#15803d" },
        }),
        actions: (row) => [
          {
            label: "Edit",
            icon: "mdi-pencil-outline",
            color: "#0a5a90",
            onClick: () => this.openEdit(row),
          },
          {
            label: row.archived ? "Unarchive" : "Archive",
            icon: "mdi-archive-outline",
            color: "#64748b",
            onClick: () => this.toggleArchive(row),
          },
        ],
      };
    },
  },

  created() {
    this.fetchAccounts();
  },

  methods: {
    async fetchAccounts() {
      this.loading = true;
      try {
        await this.$store.dispatch("accounts/getAccounts");
      } catch (err) {
        this.$toast?.error("Failed to load chart of accounts");
      } finally {
        this.loading = false;
      }
    },

    onAdd() {
      this.editing = null;
      this.form = { code: "", name: "", type: "", subType: "", description: "" };
      this.formDialog = true;
    },

    openEdit(row) {
      this.editing = row;
      this.form = {
        code: row.code,
        name: row.name,
        type: row.type,
        subType: row.subType,
        description: row.description || "",
      };
      this.formDialog = true;
    },

    onTypeChange() {
      // Changing type invalidates whatever sub-type was picked for the
      // previous type — force a re-pick rather than leaving a mismatched
      // combination selected.
      this.form.subType = "";
    },

    async submit() {
      if (!this.$refs.accountForm.validate()) return;

      this.saving = true;
      try {
        if (this.editing) {
          await this.$store.dispatch("accounts/updateAccount", {
            id: this.editing._id,
            accountData: { name: this.form.name, description: this.form.description },
          });
          this.$toast?.success("Account updated");
        } else {
          await this.$store.dispatch("accounts/createAccount", { ...this.form });
          this.$toast?.success("Account added");
        }
        this.formDialog = false;
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to save account");
      } finally {
        this.saving = false;
      }
    },

    async toggleArchive(row) {
      try {
        await this.$store.dispatch("accounts/updateAccount", {
          id: row._id,
          accountData: { archived: !row.archived },
        });
        this.$toast?.success(row.archived ? "Account restored" : "Account archived");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to update account");
      }
    },
  },
};
</script>
